import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';
import { formatCurrencyExtended } from '../../utils';

/** In-memory cache for holder charts. */
const holderWalletCache = new Map();

/** 
 * Fetch raw data from your endpoint for the holder's wallet total value.
 * You can move this to a separate file if desired.
 */
async function fetchHolderWalletTotalValue(holderAddress, timeFrame) {
  try {
    const response = await axios.get(
      `https://radar-defi-api.onrender.com/history/chart/totalWalletValue/${holderAddress}/${timeFrame}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching holder wallet total value for ${holderAddress}:`, error);
    return [];
  }
}


/**
 * Fetch raw data of all holder wallet data
 */

async function fetchHolderWallet(holderAddress, timeFrame) {
  try {
    const response = await axios.get(
      `https://radar-defi-api.onrender.com/history/holder/${holderAddress}/${timeFrame}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching holder wallet total value for ${holderAddress}:`, error);
    return [];
  }
}


/**
 * Retrieves wallet total value from the API, with a simple 1-hour cache to reduce calls.
 */
async function getHolderWalletTotalValue(holderAddress, timeFrame) {
  const cacheKey = `${holderAddress}_${timeFrame}`;
  const cacheEntry = holderWalletCache.get(cacheKey);
  const ONE_HOUR = 60 * 60 * 1000;
  const now = Date.now();

  // Return cached data if it's still "fresh".
  if (cacheEntry && (now - cacheEntry.timestamp) < ONE_HOUR) {
    return cacheEntry.data;
  }

  // Otherwise, fetch from the API and store in cache.
  const data = await fetchHolderWalletTotalValue(holderAddress, timeFrame);
  holderWalletCache.set(cacheKey, { data, timestamp: now });
  return data;
}


/**
 * Fetch raw data of all holder wallet data
 */

async function getHolderWallet(holderAddress, timeFrame) {
  const cacheKey = `${holderAddress}_${timeFrame}`;
  const cacheEntry = holderWalletCache.get(cacheKey);
  const ONE_HOUR = 60 * 60 * 1000;
  const now = Date.now();

  if (cacheEntry && (now - cacheEntry.timestamp) < ONE_HOUR) {
    return cacheEntry.data;
  }

  const data = await fetchHolderWallet(holderAddress, timeFrame);
  holderWalletCache.set(cacheKey, { data, timestamp: now });
  return data;
}

/**
 * A tiny chart component that shows the wallet's total value over time.
 * 
 * Props:
 *   - holderAddress: the wallet address to chart
 *   - timeFrame: e.g. '6h', '24h', etc. (default '24h')
 */
const TinyHolderCharts = ({ holderAddress, timeFrame = '24h', }) => {
  const [chartData, setChartData] = useState([]);

  // Fetch the chart data on mount or whenever holderAddress/timeFrame change.
  useEffect(() => {
    let mounted = true;
    if (holderAddress) {
      getHolderWalletTotalValue(holderAddress, timeFrame)
        .then((data) => {
          if (mounted) {
            setChartData(data);
          }
        })
        .catch((err) => {
          console.error("Error in getHolderWalletTotalValue:", err);
        });
    }
    return () => {
      mounted = false;
    };
  }, [holderAddress, timeFrame]);

  // Transform the raw data into Recharts-friendly format.
  // Future expansion: if you add more metrics, you can store them here too.
  const dataForChart = useMemo(() => {
    return chartData.map((item) => ({
      time: new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      walletTotalValue: parseFloat(item.walletTotalValue) || 0
    }));
  }, [chartData]);

  // Return null if there's no data to display
  if (dataForChart.length === 0) {
    return null;
  }

  // A minimal custom tooltip to show time + wallet value.
  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const val = payload[0].value;
    return (
      <div style={{
        backgroundColor: '#222',
        color: '#fff',
        padding: '6px 10px',
        borderRadius: '6px',
        fontSize: '10px'
      }}>
        <div> {label}</div>
        <div>
          {formatCurrencyExtended(val)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: '100%', maxWidth: '100px', }}>
      <ResponsiveContainer width="100%" height={40}>
        <LineChart
          data={dataForChart}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          {/* Hide axes for a tiny chart */}
          <XAxis dataKey="time" hide />
          <YAxis hide domain={['auto', 'auto']} />

          {/* Single metric => single line */}
          <Tooltip content={renderTooltip} />
          <Line
            type="monotone"
            dataKey="walletTotalValue"
            stroke="#42A5F5"
            dot={false}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TinyHolderCharts;
